import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ReviewCard from "@/components/feature/ReviewCard";
import BackToTop from "@/components/feature/BackToTop";
import { reviewSites, reviewDimensions } from "@/mocks/reviews";

function scoreColor(score: number) {
  if (score >= 4.5) return "bg-accent-100 text-accent-900";
  if (score >= 3.5) return "bg-secondary-100 text-secondary-900";
  if (score >= 3) return "bg-background-200 text-foreground-800";
  return "bg-red-100 text-red-700";
}

function ScoreBar({ label, score, desc }: { label: string; score: number; desc: string }) {
  return (
    <div className="rounded-xl border border-background-200 bg-background-50 p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-foreground-950">{label}</span>
        <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${scoreColor(score)}`}>
          {score}
        </span>
      </div>
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-background-200">
        <div
          className={`h-full rounded-full ${score >= 4.5 ? "bg-accent-500" : score >= 3.5 ? "bg-secondary-500" : score >= 3 ? "bg-foreground-400" : "bg-red-500"}`}
          style={{ width: `${(score / 5) * 100}%` }}
        ></div>
      </div>
      <p className="mt-2 text-xs leading-5 text-foreground-600">{desc}</p>
    </div>
  );
}

export default function ReviewDetail() {
  const { slug } = useParams();
  const site = reviewSites.find((s) => s.slug === slug);

  if (!site) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="mx-auto flex w-full max-w-7xl flex-col items-center px-4 py-24 text-center md:px-6">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-search-line text-2xl"></i>
          </span>
          <h1 className="mt-4 font-display text-2xl text-foreground-950">未找到该评测</h1>
          <p className="mt-2 text-sm text-foreground-600">你要查看的站点评测不存在或已下架。</p>
          <Link to="/rankings" className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600">
            返回榜单
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const related = reviewSites.filter((s) => s.slug !== site.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6">
        <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/rankings" className="hover:text-primary-600">综合榜单</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-900">{site.name}</span>
        </nav>

        {/* header */}
        <div className="mt-6 overflow-hidden rounded-2xl border border-background-200 bg-background-100">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="relative min-h-[240px]">
              <img
                src={site.img}
                alt={`${site.name}漫画网站评测封面图`}
                title={`${site.name} · 漫画网站详细评测`}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </div>
            <div className="flex flex-col justify-center p-6 md:p-8">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">{site.category}</span>
                {site.badge && (
                  <span className={`rounded-full px-3 py-1 text-xs font-medium ${site.badge === "避雷警示" ? "bg-red-500 text-background-50" : "bg-secondary-500 text-background-50"}`}>
                    {site.badge}
                  </span>
                )}
              </div>
              <h1 className="mt-4 font-display text-3xl text-foreground-950">{site.name}</h1>
              <p className="mt-1 text-sm text-secondary-700">{site.tagline}</p>
              <p className="mt-4 text-sm leading-7 text-foreground-600">{site.summary}</p>

              <div className="mt-6 flex items-center gap-6">
                <div className="flex items-center gap-1.5">
                  <i className="ri-star-fill text-accent-500"></i>
                  <span className="font-display text-2xl text-foreground-950">{site.rating}</span>
                  <span className="text-xs text-foreground-500">/ 5.0</span>
                </div>
                <div className="h-8 w-px bg-background-300"></div>
                <div className="flex flex-col">
                  <span className="text-xs text-foreground-500">一句话结论</span>
                  <span className="mt-0.5 text-sm font-medium text-foreground-900">
                    {site.rating >= 4.5 ? "值得推荐" : site.rating >= 3.5 ? "可以考虑" : "建议谨慎"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* scores */}
        <section className="mt-8">
          <h2 className="font-display text-xl text-foreground-950 md:text-2xl">评分详情</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <ScoreBar label={reviewDimensions[0].label} score={site.ads.score} desc={site.ads.desc} />
            <ScoreBar label={reviewDimensions[1].label} score={site.speed.score} desc={site.speed.desc} />
            <ScoreBar label={reviewDimensions[2].label} score={site.content.score} desc={site.content.desc} />
            <ScoreBar label={reviewDimensions[3].label} score={site.sincere.score} desc={site.sincere.desc} />
          </div>
        </section>

        {/* description */}
        <section className="mt-8">
          <h2 className="font-display text-xl text-foreground-950 md:text-2xl">详细体验</h2>
          <p className="mt-4 text-sm leading-8 text-foreground-700">{site.desc}</p>
        </section>

        {/* pros / cons */}
        <section className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="rounded-xl border border-accent-200 bg-accent-50 p-5">
            <h3 className="flex items-center gap-2 font-display text-lg text-accent-900">
              <i className="ri-checkbox-circle-line text-accent-600"></i>
              优点
            </h3>
            <ul className="mt-3 space-y-2">
              {site.pros.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-foreground-700">
                  <i className="ri-check-line mt-0.5 text-accent-600"></i>
                  {p}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-red-200 bg-red-50/50 p-5">
            <h3 className="flex items-center gap-2 font-display text-lg text-red-700">
              <i className="ri-close-circle-line"></i>
              不足
            </h3>
            <ul className="mt-3 space-y-2">
              {site.cons.map((c) => (
                <li key={c} className="flex items-start gap-2 text-sm text-foreground-700">
                  <i className="ri-close-line mt-0.5 text-red-500"></i>
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>

      {/* related */}
      <section className="border-t border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl text-foreground-950 md:text-2xl">更多评测推荐</h2>
            <Link to="/rankings" className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700">
              全部榜单 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((s) => (
              <ReviewCard key={s.slug} site={s} />
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
}